import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    this.setAttribute('initialized', '');
    this.#updateLoadMoreState();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  loadNextPage = async (event) => {
    if (event?.preventDefault) event.preventDefault();

    const button = this.refs.loadMoreButton;
    if (button instanceof HTMLButtonElement) {
      if (button.disabled) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    await super.loadNextPage();
    this.#resetPageParam();
    this.#updateLoadMoreState();

    if (button instanceof HTMLButtonElement && this.hasNextPage()) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  #updateLoadMoreState() {
    const button = this.refs.loadMoreButton;
    if (!(button instanceof HTMLButtonElement)) return;

    const hasNext = this.hasNextPage();
    button.classList.toggle('hidden', !hasNext);
    button.disabled = !hasNext;
    button.removeAttribute('aria-busy');
  }

  #handleFilterUpdate = () => {
    requestIdleCallback(() => {
      this.#updateLoadMoreState();
    });
  };

  #resetPageParam() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('page')) return;

    url.searchParams.delete('page');
    url.hash = '';
    history.replaceState('', '', url.toString());
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
